/**
 * Componente: Historial de Compras
 * Ruta: /historial-compras
 * 
 * Vista completa del historial de compras de licencias que incluye:
 * - Tarjetas de resumen con estadísticas clave
 * - Sistema de filtros avanzados (fecha, estado, tipo, búsqueda)
 * - Tabla con historial paginado y ordenable
 * - Modal de detalles completos de cada compra
 * - Descarga de facturas en PDF
 * 
 * 📡 Integración Backend:
 * Este componente consume datos del servicio purchaseService.js
 * 
 * ⚠️ Estado actual: Usando datos MOCK para desarrollo
 * ✅ Para producción: Ver comentarios en purchaseService.js
 * 
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDSnackbar from 'components/MDSnackbar';
import { Card, Icon, CircularProgress } from '@mui/material';

// Componentes locales
import PurchaseSummaryCards from './components/PurchaseSummaryCards';
import PurchaseFilters from './components/PurchaseFilters';
import PurchaseTable from './components/PurchaseTable';
import PurchaseDetailModal from './components/PurchaseDetailModal';

// Servicios
import {
  getPurchaseHistory,
  getPurchaseSummary,
  getPurchaseDetails,
  downloadInvoice,
} from 'services/purchaseService';

// Estado inicial de filtros
const INITIAL_FILTERS = {
  search: '',
  status: '',
  type: '',
  dateFrom: '',
  dateTo: '',
};

function HistorialCompras() {
  const navigate = useNavigate();

  // Estados principales
  const [loading, setLoading] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  
  // Estados del modal
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  
  // Estados de notificaciones
  const [snackbar, setSnackbar] = useState({
    open: false,
    color: 'success',
    icon: 'check',
    title: '',
    content: '',
  });

  /**
   * Carga el resumen de estadísticas
   */
  const loadSummary = useCallback(async () => {
    try {
      const response = await getPurchaseSummary();
      if (response.success) {
        setSummary(response.data);
      }
    } catch (err) {
      console.error('Error al cargar resumen:', err);
      showNotification('error', 'Error', 'No se pudo cargar el resumen de compras');
    }
  }, []);

  /**
   * Carga el historial de compras con filtros y paginación
   */
  const loadPurchaseHistory = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page,
        limit: 10,
        ...filters,
      };

      const response = await getPurchaseHistory(params);
      
      if (response.success) {
        setPurchases(response.data.purchases);
        setPagination(response.data.pagination);
        setCurrentPage(page);
      }
    } catch (err) {
      console.error('Error al cargar historial:', err);
      setError('No se pudo cargar el historial de compras. Por favor, intenta nuevamente.');
      showNotification('error', 'Error', 'No se pudo cargar el historial de compras');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  /**
   * Carga los detalles de una compra específica
   */
  const loadPurchaseDetails = useCallback(async (purchaseId) => {
    try {
      setLoadingDetails(true);
      const response = await getPurchaseDetails(purchaseId);
      
      if (response.success) {
        setSelectedPurchase(response.data);
        setModalOpen(true);
      }
    } catch (err) {
      console.error('Error al cargar detalles:', err);
      showNotification('error', 'Error', 'No se pudieron cargar los detalles de la compra');
    } finally {
      setLoadingDetails(false);
    }
  }, []);

  /**
   * Descarga la factura de una compra
   */
  const handleDownloadInvoice = useCallback(async (invoiceNumber) => {
    try {
      showNotification('info', 'Descargando...', 'Preparando tu factura');
      
      const blob = await downloadInvoice(invoiceNumber);
      
      // Crear un link temporal para descargar el archivo
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Factura_${invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      showNotification('success', 'Éxito', 'Factura descargada correctamente');
    } catch (err) {
      console.error('Error al descargar factura:', err);
      showNotification('error', 'Error', 'No se pudo descargar la factura');
    }
  }, []);

  /**
   * Handlers
   */
  const handleFilterChange = useCallback((newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1); // Resetear a la primera página
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters(INITIAL_FILTERS);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page) => {
    loadPurchaseHistory(page);
  }, [loadPurchaseHistory]);

  const handleViewDetails = useCallback((purchaseId) => {
    loadPurchaseDetails(purchaseId);
  }, [loadPurchaseDetails]);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setSelectedPurchase(null);
  }, []);

  const handleBuyLicenses = useCallback(() => {
    navigate('/gestion-licencias/comprar');
  }, [navigate]);

  const showNotification = (color, title, content) => {
    setSnackbar({
      open: true,
      color,
      icon: color === 'success' ? 'check' : color === 'error' ? 'warning' : 'info',
      title,
      content,
    });
  };

  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  /**
   * Efectos
   */
  // Cargar resumen al montar
  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  // Cargar historial cuando cambian los filtros
  useEffect(() => {
    loadPurchaseHistory(currentPage);
  }, [filters, loadPurchaseHistory]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      
      <MDBox py={3}>
        {/* Encabezado */}
        <MDBox mb={3} display="flex" justifyContent="space-between" alignItems="center">
          <MDBox>
            <MDTypography variant="h4" fontWeight="bold" gutterBottom>
              Historial de Compras
            </MDTypography>
            <MDTypography variant="button" color="text">
              Consulta y gestiona el historial completo de tus compras de licencias
            </MDTypography>
          </MDBox>
          <MDButton
            variant="gradient"
            color="info"
            startIcon={<Icon>add_shopping_cart</Icon>}
            onClick={handleBuyLicenses}
          >
            Comprar Licencias
          </MDButton>
        </MDBox>

        {/* Tarjetas de Resumen */}
        <PurchaseSummaryCards summary={summary} loading={!summary} />

        {/* Filtros */}
        <PurchaseFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
        />

        {/* Tabla de Historial */}
        {error ? (
          <Card sx={{ p: 3 }}>
            <MDBox display="flex" flexDirection="column" alignItems="center" py={5}>
              <Icon fontSize="large" color="error" sx={{ mb: 2 }}>
                error_outline
              </Icon>
              <MDTypography variant="h6" color="error" mb={1}>
                {error}
              </MDTypography>
              <MDButton
                variant="outlined"
                color="info"
                onClick={() => loadPurchaseHistory(currentPage)}
                sx={{ mt: 2 }}
              >
                Reintentar
              </MDButton>
            </MDBox>
          </Card>
        ) : (
          <PurchaseTable
            purchases={purchases}
            pagination={pagination}
            onPageChange={handlePageChange}
            onViewDetails={handleViewDetails}
            onDownloadInvoice={handleDownloadInvoice}
            loading={loading}
          />
        )}

        {/* Modal de Detalles */}
        <PurchaseDetailModal
          open={modalOpen}
          onClose={handleCloseModal}
          purchase={selectedPurchase}
          onDownloadInvoice={handleDownloadInvoice}
          loading={loadingDetails}
        />

        {/* Notificaciones */}
        <MDSnackbar
          color={snackbar.color}
          icon={snackbar.icon}
          title={snackbar.title}
          content={snackbar.content}
          dateTime=""
          open={snackbar.open}
          onClose={closeSnackbar}
          close={closeSnackbar}
          bgWhite
        />
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

export default HistorialCompras;
